import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useOrg } from '../lib/org';
import * as paymentsApi from '../api/payments';
import DonationBreakdown from '../components/DonationBreakdown';
import {
  DEFAULT_ORG_FEE_LABEL,
  LIMITS,
  computeDonationBreakdown,
  formatMoney,
} from '../lib/payments';
import { BILLING_UPGRADE_PATH } from '../lib/upgrade';
import {
  CONNECT_STATES,
  FREQUENCY_LABELS,
  METHOD_HINTS,
  METHOD_LABELS,
  centsToInput,
  parseDollarsToCents,
  parsePercent,
} from '../lib/paymentsUi';
import { ui, cn } from '../lib/ui';

/**
 * Org payments settings: /org/payments (docs/Decisions.md D44).
 * Connect Stripe, choose the payment options the plan allows (premium ones
 * are labelled and gated with an upgrade prompt), set the org's own
 * processing fee with a live breakdown preview, presets, minimum and range.
 */

const TONE = {
  neutral: ui.pillNeutral,
  info: ui.pillInfo,
  success: ui.pillSuccess,
  warning: ui.pillWarning,
  danger: ui.pillDanger,
};

function PremiumPill() {
  return <span className={ui.pillPremium}>Premium</span>;
}

function Check({ label, hint, checked, onChange, disabled, premium }) {
  return (
    <label className={cn('flex items-start gap-2 text-body-s', disabled && 'opacity-70')}>
      <input
        type="checkbox"
        className={cn(ui.checkbox, 'mt-1')}
        checked={checked}
        disabled={disabled}
        onChange={e => onChange(e.target.checked)}
      />
      <span>
        <span className="inline-flex flex-wrap items-center gap-2">
          {label}
          {premium && <PremiumPill />}
        </span>
        {hint && <span className={cn(ui.hint, 'block mt-0!')}>{hint}</span>}
      </span>
    </label>
  );
}

function ConnectCard({ status, onConnect, connecting, notice }) {
  const state = status?.state || 'not_connected';
  const info = CONNECT_STATES[state] || CONNECT_STATES.not_connected;
  const action =
    state === 'not_connected'
      ? 'Connect Stripe'
      : state === 'onboarding_incomplete'
        ? 'Continue onboarding'
        : 'Open Stripe dashboard';
  const due = [
    ...(status?.requirements?.pastDue || []),
    ...(status?.requirements?.currentlyDue || []),
  ];
  return (
    <section className={cn(ui.card, 'mb-6')} aria-labelledby="connect-heading">
      <div className={cn(ui.row, ui.spread)}>
        <h2 id="connect-heading" className={cn(ui.h2, 'mb-0!')}>
          Stripe account
        </h2>
        <span className={TONE[info.tone]} data-testid="connect-state">
          {info.label}
        </span>
      </div>
      <p className="mt-3 text-body-s">{info.text}</p>
      {status?.accountId && (
        <p className={ui.hint}>
          Charges {status.chargesEnabled ? 'enabled' : 'not enabled'}; payouts{' '}
          {status.payoutsEnabled ? 'enabled' : 'not enabled'}.
          {due.length > 0 && ` Stripe needs ${due.length} more item${due.length === 1 ? '' : 's'}.`}
        </p>
      )}
      {notice && (
        <div className={cn(ui.alertInfo, 'mt-3')} role="status">
          {notice}
        </div>
      )}
      <div className={cn(ui.row, 'mt-4')}>
        <button type="button" className={ui.btnPrimary} onClick={onConnect} disabled={connecting}>
          {connecting ? 'Opening Stripe...' : action}
        </button>
        <span className={ui.hint}>
          Donations are paid to this account. Stripe hosts onboarding and the dashboard.
        </span>
      </div>
    </section>
  );
}

function toForm(settings) {
  // allowAnonymous was the org's guest switch; sign-in is now the platform's
  // rule (docs/Decisions.md D50 item 6, D53), so it is never sent back.
  const { allowAnonymous: _retired, ...rest } = settings; // eslint-disable-line no-unused-vars
  return {
    ...rest,
    presetText: settings.presetAmountsCents.map(centsToInput).join(', '),
    minimumText: centsToInput(settings.minimumAmountCents),
    maximumText: centsToInput(settings.maximumAmountCents),
    feePercentText: String(settings.orgFee.percent ?? 0),
    feeLabel: settings.orgFee.label || '',
  };
}

/** Form state -> PUT body, or a list of input problems. */
function fromForm(form) {
  const problems = [];
  const presets = form.presetText
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(parseDollarsToCents);
  if (presets.some(p => p === null))
    problems.push('Preset amounts must be amounts like 25 or 25.50, separated by commas.');
  const minimumAmountCents = parseDollarsToCents(form.minimumText);
  if (minimumAmountCents === null) problems.push('Enter a minimum amount like 5.00.');
  const maximumAmountCents = parseDollarsToCents(form.maximumText);
  if (maximumAmountCents === null) problems.push('Enter a maximum amount like 1000.00.');
  const percent = parsePercent(form.feePercentText);
  if (percent === null)
    problems.push('Enter the fee percentage as a number with at most two decimals.');
  return {
    problems,
    body: {
      enabled: form.enabled,
      presetAmountsCents: presets.filter(p => p !== null),
      minimumAmountCents,
      maximumAmountCents,
      allowCustomAmount: form.allowCustomAmount,
      frequencies: form.frequencies,
      paymentMethods: form.paymentMethods,
      donorCoversFeesOption: form.donorCoversFeesOption,
      orgFee: { percent, label: form.feeLabel.trim() },
    },
  };
}

function toggle(list, value, on) {
  const set = new Set(list);
  if (on) set.add(value);
  else set.delete(value);
  return [...set];
}

function FeePreview({ body, platformFeeRate, premium }) {
  const ids = useId();
  const [exampleText, setExampleText] = useState('');
  const example = parseDollarsToCents(exampleText) ?? body.presetAmountsCents[0] ?? 2500;
  const [covers, setCovers] = useState(false);
  const preview = useMemo(() => {
    try {
      return computeDonationBreakdown({
        amountCents: Math.max(example, LIMITS.minDonationCents),
        orgFee: body.orgFee,
        platformFeeRate,
        donorCoversFees: premium && covers,
      });
    } catch {
      return null;
    }
  }, [example, body.orgFee, platformFeeRate, premium, covers]);

  return (
    <div className="grid gap-3">
      <label className={cn(ui.field, 'mb-0!')} htmlFor={`${ids}-example`}>
        <span className={ui.label}>Preview a donation of (USD)</span>
        <input
          id={`${ids}-example`}
          className={ui.input}
          inputMode="decimal"
          value={exampleText}
          placeholder={centsToInput(example)}
          onChange={e => setExampleText(e.target.value)}
        />
      </label>
      {premium && (
        <Check label="Preview with the donor covering fees" checked={covers} onChange={setCovers} />
      )}
      {preview ? (
        <DonationBreakdown breakdown={preview} title="What the donor sees, and what you receive" />
      ) : (
        <p className={ui.hint}>Fix the fee values to see a preview.</p>
      )}
      <p className={ui.hint}>
        The platform fee for your plan is {platformFeeRate} percent of each donation. Stripe
        processing is an estimate (card 2.9 percent plus {formatMoney(30)}; bank 0.8 percent, at
        most {formatMoney(500)}; recurring adds 0.7 percent).
      </p>
    </div>
  );
}

export default function OrgPayments() {
  const { activeOrg } = useOrg();
  const ids = useId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [notice, setNotice] = useState(null);
  const isOrgAdmin = activeOrg?.role === 'org-admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsData, connectData] = await Promise.all([
        paymentsApi.getPaymentSettings(),
        paymentsApi.getConnectStatus(),
      ]);
      setData(settingsData);
      setStatus(connectData.status);
      setForm(toForm(settingsData.settings));
    } catch (err) {
      setError(err.message || 'Could not load payment settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOrgAdmin) load();
  }, [isOrgAdmin, load, activeOrg?.orgId]);

  useEffect(() => {
    const connect = searchParams.get('connect');
    if (!connect) return;
    setNotice(
      connect === 'refresh'
        ? 'The Stripe link expired. Select Continue onboarding to get a new one.'
        : 'Welcome back from Stripe. The status below is current.'
    );
    searchParams.delete('connect');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  async function handleConnect() {
    setConnecting(true);
    setError(null);
    try {
      const base = `${window.location.origin}/org/payments`;
      const res = await paymentsApi.createConnectLink(
        `${base}?connect=return`,
        `${base}?connect=refresh`
      );
      window.location.assign(res.url);
    } catch (err) {
      setError(err.message || 'Could not open Stripe');
      setConnecting(false);
    }
  }

  const parsed = form ? fromForm(form) : null;

  async function handleSave() {
    if (!parsed || parsed.problems.length) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const res = await paymentsApi.updatePaymentSettings(parsed.body);
      setData(d => ({ ...d, settings: res.settings, options: res.options }));
      setForm(toForm(res.settings));
      setSaved(true);
    } catch (err) {
      setError(err.message || 'Could not save payment settings');
    } finally {
      setSaving(false);
    }
  }

  if (!activeOrg || !isOrgAdmin) {
    return (
      <div data-zone="console">
        <h1 className={ui.h1}>Payments and donations</h1>
        <div className={ui.alertInfo}>Only organization administrators can manage payments.</div>
      </div>
    );
  }

  if (loading && !form) {
    return (
      <div data-zone="console">
        <h1 className={ui.h1}>Payments and donations</h1>
        <p className={ui.muted} role="status">
          Loading...
        </p>
      </div>
    );
  }

  if (!form) {
    return (
      <div data-zone="console">
        <h1 className={ui.h1}>Payments and donations</h1>
        <div className={ui.alertError} role="alert">
          {error}
        </div>
      </div>
    );
  }

  const options = data.options;
  const premium = options.plan === 'premium';
  const set = patch => {
    setSaved(false);
    setForm(f => ({ ...f, ...patch }));
  };
  // Only a verified nonprofit may take donations (docs/Decisions.md D64);
  // blockedReason says what is missing.
  const blockedReason = data.blockedReason || null;
  const canEnable = Boolean(status?.chargesEnabled) && !blockedReason;

  return (
    <div data-zone="console">
      <div className={cn(ui.row, ui.spread, 'mb-4')}>
        <h1 className={cn(ui.h1, 'mb-0!')}>Payments and donations</h1>
        <div className={ui.row}>
          <Link to="/org/donations" className={cn(ui.btnSecondary, ui.btnSm)}>
            View donations
          </Link>
          <Link to={`/o/${activeOrg.slug}/donate`} className={cn(ui.btnGhost, ui.btnSm)}>
            Open donate page
          </Link>
        </div>
      </div>

      {error && (
        <div className={ui.alertError} role="alert">
          {error}
        </div>
      )}

      {blockedReason && (
        <div
          className={cn(ui.alertWarning, 'mb-4 flex flex-wrap items-center gap-3')}
          data-testid="payments-type-blocked"
        >
          <span className="flex-1">{blockedReason}</span>
          <Link to="/org/settings#org-type" className={cn(ui.btnSecondary, ui.btnSm)}>
            Organization type and profile
          </Link>
        </div>
      )}

      {data.disclosure && (
        <section className={cn(ui.card, 'mb-6')} aria-labelledby="disclosure-heading">
          <h2 id="disclosure-heading" className={ui.h2}>
            What donors read
          </h2>
          <p className={cn(ui.muted, 'text-body-s')}>
            From your verified nonprofit profile, shown on your donate page and every receipt. Change
            it in Org settings; the change applies once the platform team verifies it.
          </p>
          <p className="mb-1">
            <strong>{data.disclosure.legalName}</strong> (EIN {data.disclosure.ein})
          </p>
          <p className="mb-1 text-body-s">{data.disclosure.statement}</p>
          {data.disclosure.caRegistryNumber && (
            <p className="m-0 text-body-s">
              California Registry of Charities and Fundraisers number{' '}
              {data.disclosure.caRegistryNumber}
            </p>
          )}
        </section>
      )}

      <ConnectCard
        status={status}
        onConnect={handleConnect}
        connecting={connecting}
        notice={notice}
      />

      {!premium && (
        <div
          className={cn(ui.alertInfo, 'flex flex-wrap items-center gap-3')}
          data-testid="payments-upgrade"
        >
          <PremiumPill />
          <span className="flex-1">{data.upgradeMessage}</span>
          <Link to={BILLING_UPGRADE_PATH} className={cn(ui.btnAccent, ui.btnSm)}>
            Upgrade plan
          </Link>
        </div>
      )}

      <section className={cn(ui.card, 'mb-6')} aria-labelledby="options-heading">
        <h2 id="options-heading" className={ui.h2}>
          Donation options
        </h2>

        <div className="grid gap-4">
          <Check
            label="Accept donations on the public donate page"
            hint={
              canEnable
                ? 'Donors can give at your donate page.'
                : blockedReason || 'Connect Stripe and finish onboarding first.'
            }
            checked={form.enabled}
            disabled={!canEnable && !form.enabled}
            onChange={v => set({ enabled: v })}
          />
          <p className={cn(ui.muted, 'text-body-s')}>
            {data.requireSignIn
              ? 'Donors sign in to a registered account before they give. The platform sets this rule.'
              : 'Donors can give as guests for this organization. The platform sets this rule.'}{' '}
            Each gift must be from {formatMoney(options.limits.minAmountCents)} to{' '}
            {formatMoney(options.limits.maxAmountCents)}.
          </p>

          <div className={cn(ui.grid, ui.gridMd2)}>
            <label className={ui.field} htmlFor={`${ids}-presets`}>
              <span className={ui.label}>Preset amounts (USD)</span>
              <input
                id={`${ids}-presets`}
                className={ui.input}
                value={form.presetText}
                onChange={e => set({ presetText: e.target.value })}
                placeholder="10, 25, 50, 100"
              />
              <span className={ui.hint}>
                Up to {options.maxPresetAmounts}, separated by commas.
              </span>
            </label>
            <label className={ui.field} htmlFor={`${ids}-min`}>
              <span className={ui.label}>Minimum donation (USD)</span>
              <input
                id={`${ids}-min`}
                className={ui.input}
                inputMode="decimal"
                value={form.minimumText}
                onChange={e => set({ minimumText: e.target.value })}
              />
              <span className={ui.hint}>
                At least {formatMoney(options.limits.minAmountCents)}. Every preset must meet it.
              </span>
            </label>
          </div>

          <Check
            label="Let donors enter their own amount"
            hint="Within the minimum and the maximum you set."
            checked={form.allowCustomAmount}
            disabled={!options.customAmount}
            premium={!premium}
            onChange={v => set({ allowCustomAmount: v })}
          />
          {form.allowCustomAmount && (
            <label className={cn(ui.field, 'max-w-sm')} htmlFor={`${ids}-max`}>
              <span className={ui.label}>Maximum donation (USD)</span>
              <input
                id={`${ids}-max`}
                className={ui.input}
                inputMode="decimal"
                value={form.maximumText}
                onChange={e => set({ maximumText: e.target.value })}
              />
              <span className={ui.hint}>At most {formatMoney(options.limits.maxAmountCents)}.</span>
            </label>
          )}

          <fieldset className="grid gap-2">
            <legend className={ui.label}>Frequencies</legend>
            <Check label={FREQUENCY_LABELS.one_time} checked disabled onChange={() => {}} />
            {['month', 'year'].map(f => (
              <Check
                key={f}
                label={`${FREQUENCY_LABELS[f]} recurring`}
                checked={form.frequencies.includes(f)}
                disabled={!options.frequencies.includes(f)}
                premium={!premium}
                onChange={v => set({ frequencies: toggle(form.frequencies, f, v) })}
              />
            ))}
          </fieldset>

          <fieldset className="grid gap-2">
            <legend className={ui.label}>Payment methods</legend>
            <Check
              label={METHOD_LABELS.card}
              hint={METHOD_HINTS.card}
              checked
              disabled
              onChange={() => {}}
            />
            {['us_bank_account', 'link'].map(m => (
              <Check
                key={m}
                label={METHOD_LABELS[m]}
                hint={METHOD_HINTS[m]}
                checked={form.paymentMethods.includes(m)}
                disabled={!options.paymentMethods.includes(m)}
                premium={!premium}
                onChange={v => set({ paymentMethods: toggle(form.paymentMethods, m, v) })}
              />
            ))}
          </fieldset>

          <Check
            label="Offer donors the option to cover the fees"
            hint="The donor adds the platform and processing fees so you receive the full donation."
            checked={form.donorCoversFeesOption}
            disabled={!options.donorCoversFees}
            premium={!premium}
            onChange={v => set({ donorCoversFeesOption: v })}
          />
        </div>
      </section>

      <section className={cn(ui.card, 'mb-6')} aria-labelledby="fee-heading">
        <h2 id="fee-heading" className={ui.h2}>
          Your organization fee
        </h2>
        <p className={cn(ui.muted, 'mb-4 text-body-s')}>
          Optional. A percentage of each donation your organization keeps toward its own platform
          and processing costs. It is taken from the donation, not added on top, and the donor sees
          it before they pay, beside the platform fee and Stripe&apos;s processing fee, so they know
          which fees are the platform&apos;s and which are yours.
        </p>
        <div className={cn(ui.grid, ui.gridMd2)}>
          <label className={ui.field} htmlFor={`${ids}-fee-pct`}>
            <span className={ui.label}>Percent of the donation</span>
            <input
              id={`${ids}-fee-pct`}
              className={ui.input}
              inputMode="decimal"
              value={form.feePercentText}
              onChange={e => set({ feePercentText: e.target.value })}
            />
            <span className={ui.hint}>
              0 to {options.orgFeePercentMax ?? LIMITS.orgFeePercentMax}. The platform caps the
              platform and organization fees together.
            </span>
          </label>
          <label className={ui.field} htmlFor={`${ids}-fee-label`}>
            <span className={ui.label}>Label shown to donors</span>
            <input
              id={`${ids}-fee-label`}
              className={ui.input}
              maxLength={LIMITS.orgFeeLabelMaxLength}
              value={form.feeLabel}
              placeholder={DEFAULT_ORG_FEE_LABEL}
              onChange={e => set({ feeLabel: e.target.value })}
            />
          </label>
        </div>
        <FeePreview body={parsed.body} platformFeeRate={data.platformFeeRate} premium={premium} />
      </section>

      {parsed.problems.length > 0 && (
        <div className={ui.alertWarning} role="status">
          {parsed.problems.join(' ')}
        </div>
      )}
      {saved && (
        <div className={ui.alertSuccess} role="status">
          Payment settings saved.
        </div>
      )}
      <button
        type="button"
        className={ui.btnPrimary}
        onClick={handleSave}
        disabled={saving || parsed.problems.length > 0}
      >
        {saving ? 'Saving...' : 'Save payment settings'}
      </button>
    </div>
  );
}
